import { LocationIcon } from '@/assets/icons'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { formatTimeOfDay } from '@lib/datetime'
import { iconSizes } from '@/theme/tokens'
import { Event } from '@types'
import React from 'react'
import { EventAddress, EventBody, EventCard, EventMeta, EventName, EventRowRoot, PinGlyph, RailColumn, RailDot, TimePill } from './DayPane.styles'
import { eventAddress } from '../core/timeline'
import EventMenu from './EventMenu'
import { TestIds } from '@/testIds'

export interface EventRowProps {
	event: Event
	/** The event's 1-based place in the day, as its map pin numbers it. */
	ordinal: number
	first: boolean
	last: boolean
	mapped: boolean
	highlighted: boolean
	onOpen: (event: Event) => void
}

export interface EditableEventRowProps extends EventRowProps {
	onMove: (event: Event) => void
	onDelete: (event: Event) => void
}

/** What dnd-kit hands a sortable row: its drag handle wiring and where it is mid-drag. */
type DragHandle = Pick<ReturnType<typeof useSortable>, 'attributes' | 'listeners' | 'setNodeRef' | 'transform' | 'transition' | 'isDragging'>

/** The row itself; `sortable` and `menu` are what editing adds to it. */
const EventRowView: React.FC<EventRowProps & { sortable?: DragHandle; menu?: React.ReactNode }> = ({
	event,
	ordinal,
	first,
	last,
	mapped,
	highlighted,
	onOpen,
	sortable,
	menu,
}) => {
	const time = formatTimeOfDay(event.startTime)
	const address = eventAddress(event.location)

	return (
		<EventRowRoot
			ref={sortable?.setNodeRef}
			$isDragging={sortable?.isDragging ?? false}
			data-event-id={event.id}
			$transform={sortable ? CSS.Transform.toString(sortable.transform) : undefined}
			$transition={sortable?.transition}
		>
			<RailColumn $first={first} $last={last}>
				<RailDot aria-hidden="true">{ordinal}</RailDot>
			</RailColumn>
			<EventCard
				$highlighted={highlighted}
				$sortable={!!sortable}
				role={sortable ? undefined : 'button'}
				tabIndex={sortable ? undefined : 0}
				{...sortable?.attributes}
				{...sortable?.listeners}
				aria-label={event.name}
				data-testid={TestIds.itinerary.eventCell(event.name)}
				onClick={() => onOpen(event)}
				onKeyDown={e => {
					// Only the card's own key press opens it, not one from a control inside it.
					if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) {
						e.preventDefault()
						onOpen(event)
					}
				}}
			>
				<EventBody>
					<EventName>{event.name}</EventName>
					{(time || address) && (
						<EventMeta>
							{time && <TimePill>{time}</TimePill>}
							{address && (
								<>
									<PinGlyph $mapped={mapped} title={mapped ? undefined : 'Not on the map yet'}>
										<LocationIcon sx={{ fontSize: iconSizes.xs }} />
									</PinGlyph>
									<EventAddress>{address}</EventAddress>
								</>
							)}
						</EventMeta>
					)}
				</EventBody>
				{menu}
			</EventCard>
		</EventRowRoot>
	)
}

/** One event on the day's rail, read-only: its number on the line and the card beside it, which opens the event. */
export const EventRow: React.FC<EventRowProps> = props => <EventRowView {...props} />

/**
 * One event on the day's rail, editable: the card is also the sortable drag
 * handle. Click opens the event; the "⋮" holds the rest.
 */
export const EditableEventRow: React.FC<EditableEventRowProps> = ({ onMove, onDelete, ...props }) => {
	const sortable = useSortable({ id: props.event.id })
	return (
		<EventRowView
			{...props}
			sortable={sortable}
			menu={
				<EventMenu
					eventName={props.event.name}
					onEdit={() => props.onOpen(props.event)}
					onMove={() => onMove(props.event)}
					onDelete={() => onDelete(props.event)}
				/>
			}
		/>
	)
}
